import { useState, useEffect } from 'react'
import api from '../../utils/api'
import AdminLayout from '../../components/admin/AdminLayout'

interface Completion {
  course_title: string
  instructor: string
  credits: number
  year: number
  season: string
  score?: number
  grade: string
  completed_at: string
}

interface User {
  id: number
  email: string
  name: string
  birth_date?: string
  organization?: string
  role: string
  created_at: string
  enrollment_count: number
  completed_count: number
  registration_source?: string
  completions?: Completion[]
}

interface PaginationData {
  total: number
  page: number
  limit: number
  totalPages: number
}

interface EditFormData {
  name: string
  email: string
  birth_date: string
  organization: string
}

type SortField = 'id' | 'name' | 'email' | 'birth_date' | 'organization' | 'created_at'
type SortOrder = 'asc' | 'desc'

const AdminUsers = () => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [pagination, setPagination] = useState<PaginationData>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  })
  const [search, setSearch] = useState('')
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set())
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [editFormData, setEditFormData] = useState<EditFormData>({
    name: '',
    email: '',
    birth_date: '',
    organization: ''
  })
  const [showEditModal, setShowEditModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [sortBy, setSortBy] = useState<SortField>('name')  // 기본 정렬: 이름
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')  // 기본: 가나다순

  useEffect(() => {
    loadUsers()
  }, [pagination.page, search, sortBy, sortOrder])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        sortBy,
        sortOrder,
        ...(search && { search })
      })
      const response = await api.get(`/admin/users?${params.toString()}`)
      setUsers(response.data.users)
      setPagination(response.data.pagination)
      setError('')
    } catch (err: any) {
      setError(err.response?.data?.error || '사용자 목록을 불러오는데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      // 같은 컬럼 클릭 시 정렬 방향 변경
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      // 다른 컬럼 클릭 시 해당 컬럼 오름차순
      setSortBy(field)
      setSortOrder('asc')
    }
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const getSortIcon = (field: SortField) => {
    if (sortBy !== field) return '↕️'
    return sortOrder === 'asc' ? '↑' : '↓'
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPagination(prev => ({ ...prev, page: 1 }))
    loadUsers()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const toggleUserExpansion = (userId: number) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev)
      if (newSet.has(userId)) {
        newSet.delete(userId)
      } else {
        newSet.add(userId)
      }
      return newSet
    })
  }

  const handleExportExcel = async () => {
    try {
      const response = await api.get('/admin/users/export', {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `회원정보_${new Date().toISOString().split('T')[0]}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      alert(err.response?.data?.error || '엑셀 다운로드에 실패했습니다.')
    }
  }

  const handleEditClick = (e: React.MouseEvent, user: User) => {
    e.stopPropagation()
    setEditingUser(user)
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      birth_date: user.birth_date || '',
      organization: user.organization || ''
    })
    setShowEditModal(true)
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingUser) return

    try {
      setSaving(true)
      await api.put(`/admin/users/${editingUser.id}`, editFormData)
      alert('회원 정보가 수정되었습니다.')
      setShowEditModal(false)
      setEditingUser(null)
      loadUsers()
    } catch (err: any) {
      alert(err.response?.data?.error || '회원 정보 수정에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteClick = async (e: React.MouseEvent, user: User) => {
    e.stopPropagation()
    
    if (!confirm(`정말로 "${user.name}" 회원을 삭제하시겠습니까?\n\n⚠️ 이 작업은 되돌릴 수 없으며, 해당 회원의 모든 수강신청, 성적, 이수 내역이 함께 삭제됩니다.`)) {
      return
    }

    try {
      await api.delete(`/admin/users/${user.id}`)
      alert('회원이 삭제되었습니다.')
      loadUsers()
    } catch (err: any) {
      alert(err.response?.data?.error || '회원 삭제에 실패했습니다.')
    }
  }

  return (
    <AdminLayout>
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ margin: 0 }}>👥 회원 관리</h1>
          <button 
            onClick={handleExportExcel}
            className="btn btn-primary"
            style={{ padding: '10px 20px' }}
          >
            📊 엑셀 다운로드
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="card" style={{ marginBottom: '20px' }}>
          <form onSubmit={handleSearch}>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <input
                type="text"
                placeholder="이름 또는 이메일로 검색"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ flex: 1 }}
              />
              <button type="submit" className="btn btn-primary">
                검색
              </button>
            </div>
          </form>
          <div style={{ fontSize: '14px', color: '#666' }}>
            총 {pagination.total}명의 회원
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            로딩 중...
          </div>
        ) : users.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '50px' }}>
            등록된 회원이 없습니다.
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('id')}
                    >
                      ID {getSortIcon('id')}
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('name')}
                    >
                      이름 {getSortIcon('name')}
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('email')}
                    >
                      이메일 {getSortIcon('email')}
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('birth_date')}
                    >
                      생년월일 {getSortIcon('birth_date')}
                    </th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('organization')}
                    >
                      소속 {getSortIcon('organization')}
                    </th>
                    <th>수강신청</th>
                    <th>이수완료</th>
                    <th>가입경로</th>
                    <th 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('created_at')}
                    >
                      가입일 {getSortIcon('created_at')}
                    </th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <>
                      <tr 
                        key={user.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleUserExpansion(user.id)}
                      >
                        <td>{user.id}</td>
                        <td>
                          {user.completions && user.completions.length > 0 && (
                            <span style={{ marginRight: '5px' }}>
                              {expandedUsers.has(user.id) ? '▼' : '▶'}
                            </span>
                          )}
                          {user.name}
                        </td>
                        <td>{user.email}</td>
                        <td>{user.birth_date || '-'}</td>
                        <td>{user.organization || '-'}</td>
                        <td style={{ textAlign: 'center' }}>{user.enrollment_count}</td>
                        <td style={{ textAlign: 'center' }}>{user.completed_count}</td>
                        <td>{user.registration_source || '온라인'}</td>
                        <td style={{ fontSize: '12px' }}>{formatDate(user.created_at)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                              onClick={(e) => handleEditClick(e, user)}
                            >
                              수정
                            </button>
                            <button
                              className="btn"
                              style={{ padding: '4px 8px', fontSize: '12px', backgroundColor: '#dc3545', color: 'white' }}
                              onClick={(e) => handleDeleteClick(e, user)}
                            >
                              삭제
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedUsers.has(user.id) && user.completions && user.completions.length > 0 && (
                        <tr>
                          <td colSpan={10} style={{ padding: '0', backgroundColor: '#f9f9f9' }}>
                            <div style={{ padding: '20px' }}>
                              <h3 style={{ marginTop: 0, marginBottom: '15px' }}>이수 내역</h3>
                              <table className="table" style={{ margin: 0 }}>
                                <thead>
                                  <tr>
                                    <th>학년도</th>
                                    <th>학기</th>
                                    <th>강좌명</th>
                                    <th>교수명</th>
                                    <th>학점</th>
                                    <th>점수</th>
                                    <th>등급</th>
                                    <th>이수완료일</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {user.completions.map((completion, idx) => (
                                    <tr key={idx}>
                                      <td>{completion.year}</td>
                                      <td>{completion.season}</td>
                                      <td>{completion.course_title}</td>
                                      <td>{completion.instructor}</td>
                                      <td>{completion.credits}</td>
                                      <td>{completion.score || '-'}</td>
                                      <td>{completion.grade}</td>
                                      <td>{completion.completed_at ? formatDate(completion.completed_at) : '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', gap: '5px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  이전
                </button>
                <span style={{ padding: '8px 16px', alignSelf: 'center' }}>
                  {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  다음
                </button>
              </div>
            )}
          </>
        )}

        {/* 수정 모달 */}
        {showEditModal && editingUser && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px' }}>회원 정보 수정</h2>
              <form onSubmit={handleEditSubmit}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>이름</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>이메일</label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>생년월일</label>
                  <input
                    type="date"
                    value={editFormData.birth_date}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, birth_date: e.target.value }))}
                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>소속</label>
                  <input
                    type="text"
                    value={editFormData.organization}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, organization: e.target.value }))}
                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEditModal(false)
                      setEditingUser(null)
                    }}
                    disabled={saving}
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? '저장 중...' : '저장'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminUsers
